import { Link } from "react-router-dom";
import SectionHeading from "@/components/feature/SectionHeading";
import type { Article } from "@/types/content";
import { formatHeat } from "@/utils/format";

interface LiveFeedSectionProps {
  liveArticles: Article[];
  timelineSource: Article;
}

export default function LiveFeedSection({ liveArticles, timelineSource }: LiveFeedSectionProps) {
  return (
    <section className="w-full px-4 py-12 md:px-8 md:py-16">
      <SectionHeading
        eyebrow="实时楼"
        title="正在更新的现场"
        desc="按时间顺序记录每一条新进展，不剪掉上下文，也不替你下结论。"
        moreTo={`/article/${timelineSource.id}`}
        moreLabel="查看完整时间线"
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="flex flex-col gap-4 lg:col-span-7">
          {liveArticles.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="group flex cursor-pointer gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
            >
              <div className="h-20 w-24 shrink-0 overflow-hidden rounded-md bg-background-100 sm:h-24 sm:w-32">
                <img
                  src={article.cover}
                  alt={`${article.title} 现场配图`}
                  title={`${article.title} 现场记录`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="min-w-0">
                <span className="inline-flex items-center gap-1.5 font-label text-xs font-medium text-primary-700">
                  <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-primary-500" />
                  {article.categoryName}
                </span>
                <h3 className="mt-1.5 text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                  {article.title}
                </h3>
                <p className="mt-1.5 line-clamp-2 hidden text-xs leading-relaxed text-foreground-600 sm:block">
                  {article.summary}
                </p>
                <span className="mt-2 inline-flex items-center gap-2 font-label text-xs text-foreground-500">
                  <span>{article.publishedAt}</span>
                  <span aria-hidden="true">·</span>
                  <span>{formatHeat(article.heat)} 热度</span>
                </span>
              </div>
            </Link>
          ))}
        </div>

        <aside className="rounded-lg border border-background-200 bg-background-100 p-5 lg:col-span-5">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-base font-bold text-foreground-950">时间线速览</h3>
            <span className="rounded-full bg-background-50 px-2.5 py-0.5 font-label text-xs text-foreground-600">
              {timelineSource.publishedAt}
            </span>
          </div>
          <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{timelineSource.title}</p>

          <ol className="mt-5 flex flex-col">
            {timelineSource.timeline.map((item, index) => (
              <li key={`${item.time}-${index}`} className="relative flex gap-3 pb-5 last:pb-0">
                <div className="flex flex-col items-center">
                  <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-accent-500" />
                  {index !== timelineSource.timeline.length - 1 && (
                    <span className="mt-1 w-px flex-1 bg-background-300" />
                  )}
                </div>
                <div className="min-w-0 pb-1">
                  <span className="font-label text-xs font-medium text-primary-700">{item.time}</span>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                </div>
              </li>
            ))}
          </ol>

          <Link
            to={`/article/${timelineSource.id}`}
            className="mt-2 inline-flex cursor-pointer items-center gap-1 font-label text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
          >
            阅读完整梳理
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </aside>
      </div>
    </section>
  );
}